import type { RouterAdapter } from '@tour-kit/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { canonicalizeShellPathname } from '@/core/router/app-paths';

/**
 * Ionic / React Router v6 adapter for TourKit multi-page tours.
 * Uses the custom RouterAdapter (stock RR6 adapters unused).
 */
export function useGuidanceRouter(): RouterAdapter {
  const location = useLocation();
  const { navigateShell } = useNavigateShell();
  const pathnameRef = useRef(canonicalizeShellPathname(location.pathname));
  const listenersRef = useRef(new Set<(route: string) => void>());

  useEffect(() => {
    const next = canonicalizeShellPathname(location.pathname);
    if (next === pathnameRef.current) return;
    pathnameRef.current = next;
    for (const listener of listenersRef.current) {
      listener(next);
    }
  }, [location.pathname]);

  const getCurrentRoute = useCallback(() => pathnameRef.current, []);

  const navigate = useCallback(
    async (route: string) => {
      navigateShell(route);
      return true;
    },
    [navigateShell],
  );

  const matchRoute = useCallback(
    (pattern: string, mode: 'exact' | 'startsWith' | 'contains' = 'exact') => {
      const current = pathnameRef.current;
      switch (mode) {
        case 'startsWith':
          return current.startsWith(pattern);
        case 'contains':
          return current.includes(pattern);
        default:
          return current === pattern;
      }
    },
    [],
  );

  const onRouteChange = useCallback((callback: (route: string) => void) => {
    callback(pathnameRef.current);
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  return useMemo(
    () => ({
      getCurrentRoute,
      navigate,
      matchRoute,
      onRouteChange,
    }),
    [getCurrentRoute, navigate, matchRoute, onRouteChange],
  );
}
